import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { panelCatalogApiRef } from '@agilelab/plugin-wb-builder-catalog';
import { useEnrichedIdentity } from '@agilelab/plugin-wb-platform';

export function useEntitiesMembership() {
  const panelCatalogApi = useApi(panelCatalogApiRef);
  const { identity: enrichedIdentity, loading: identityLoading } =
    useEnrichedIdentity();

  /**
   * It returns the flattened hierarchy of all the groups and related sub-groups
   * @param groups
   * @returns
   */
  const resolveGroupsHierarchy = async (
    groups: string[],
  ): Promise<string[]> => {
    const currentGroups = await panelCatalogApi.queryEntities({
      filter: [{ 'spec.parent': groups, kind: 'Group' }],
      fields: ['kind', 'metadata.name', 'metadata.namespace'],
    });
    if (currentGroups.items.length) {
      return [
        ...groups,
        ...(await resolveGroupsHierarchy(
          currentGroups.items.map(stringifyEntityRef),
        )),
      ];
    }

    return groups;
  };

  /** Retrieve all group entity */
  const retrieveEntityGroups = async (groups: string[]) => {
    return panelCatalogApi.queryEntities({
      filter: [
        {
          'metadata.name': groups.map(group => parseEntityRef(group).name),
          kind: 'Group',
        },
      ],
    });
  };
  /** Retrieve all users entity belong to the every group in `groups` */
  const retrieveEntityUsers = async (groups: string[]) => {
    if (groups.length === 0) {
      return { items: [] };
    }

    return panelCatalogApi.queryEntities({
      filter: [
        {
          'spec.memberOf': groups,
          kind: 'User',
        },
      ],
    });
  };

  const getMembersIdentities = async (options: {
    filter: { kind: string[]; excludeGroupNames?: string[] };
  }): Promise<Entity[]> => {
    if (!enrichedIdentity) {
      return [];
    }

    /**
     * This contains the groups and the sub-groups related to the logged user.
     */
    const overallGroups = await resolveGroupsHierarchy(
      enrichedIdentity.ownershipEntityRefs.filter(entity =>
        entity.toLowerCase().startsWith('group'),
      ),
    );
    const excludedNamesSet = new Set(
      (options.filter.excludeGroupNames ?? []).map(n => n.toLowerCase()),
    );
    const allowedGroupRefs = overallGroups.filter(
      g => !excludedNamesSet.has(parseEntityRef(g).name.toLowerCase()),
    );
    const identities: Entity[] = [];
    // Used to avoid duplicates
    const seenEntities = new Set<string>();

    for (const currentKind of options.filter.kind ?? []) {
      switch (String(currentKind).toLowerCase()) {
        case 'group': {
          const groupsResp = await retrieveEntityGroups(allowedGroupRefs);
          // Avoid duplicates
          for (const group of groupsResp.items) {
            const entityKey = stringifyEntityRef(group);
            if (!seenEntities.has(entityKey)) {
              seenEntities.add(entityKey);
              identities.push(group);
            }
          }
          break;
        }
        case 'user': {
          if (allowedGroupRefs.length === 0) break;
          const usersResp = await retrieveEntityUsers(allowedGroupRefs);
          // Avoid duplicates
          for (const user of usersResp.items) {
            const entityKey = stringifyEntityRef(user);
            if (!seenEntities.has(entityKey)) {
              seenEntities.add(entityKey);
              identities.push(user);
            }
          }
          break;
        }
        default:
          break;
      }
    }

    return identities;
  };

  return { getMembersIdentities, identityLoading };
}
